import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { logActivity } from "@/lib/activity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Typeahead } from "@/components/Typeahead";
import { toast } from "sonner";
import { UploadCloud, FileText, Download, Trash2, Search, Eye, X as XIcon } from "lucide-react";
import { PageHeader, EmptyState, formatDate } from "@/lib/ui";
import { openExternal } from "@/lib/external";
import { downloadCsv, iso } from "@/lib/csvExport";

// Business document formats only. Explicitly excludes executables and scripts.
const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "csv", "txt"];
const BLOCKED_EXT = ["exe", "bat", "cmd", "sh", "com", "scr", "msi", "app", "dmg", "js", "vbs", "ps1", "jar"];
function fileExt(filename) { return (filename.split(".").pop() || "").toLowerCase(); }
function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const TYPES = [
  { value: "intake_form", label: "Intake Form" },
  { value: "identification", label: "Identification" },
  { value: "insurance", label: "Insurance" },
  { value: "referral", label: "Referral" },
  { value: "assessment", label: "Assessment" },
  { value: "treatment_plan", label: "Treatment Plan" },
  { value: "other", label: "Other" },
];

export default function Documents() {
  const { currentOrgId, user, currentRole } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [typeFilter, setTypeFilter] = useState(params.get("type") || "all");
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [docType, setDocType] = useState("other");
  const [clientId, setClientId] = useState("none");
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const isReadOnly = currentRole === "read_only";

  // Sync filters/search to URL so Back-nav restores exact view.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (typeFilter && typeFilter !== "all") next.set("type", typeFilter); else next.delete("type");
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q, typeFilter]);

  const load = async () => {
    if (!currentOrgId) return;
    setLoading(true);
    const [d, c] = await Promise.all([
      supabase.from("documents").select("*,clients(id,first_name,last_name),profiles:uploaded_by(full_name)").eq("organization_id", currentOrgId).order("created_at", { ascending: false }),
      supabase.from("clients").select("id,first_name,last_name").eq("organization_id", currentOrgId).order("first_name"),
    ]);
    setRows(d.data || []); setClients(c.data || []); setLoading(false);
  };
  useEffect(() => { load(); }, [currentOrgId]);
  useEffect(() => { if (params.get("new") === "1") { setOpen(true); params.delete("new"); setParams(params, { replace: true }); } /* eslint-disable-next-line */ }, []);

  const upload = async () => {
    if (!file) return toast.error("Choose a file");
    if (!name.trim()) return toast.error("Document name is required");
    const ext = fileExt(file.name);
    if (BLOCKED_EXT.includes(ext)) return toast.error(`.${ext} files are not allowed for security reasons`);
    if (!ALLOWED_EXT.includes(ext)) return toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXT.join(", ").toUpperCase()}`);
    if (file.size > 25 * 1024 * 1024) return toast.error("File too large (max 25 MB)");
    setUploading(true);
    const cid = clientId === "none" ? null : clientId;
    const path = `orgs/${currentOrgId}/${cid ? `clients/${cid}/` : "general/"}${crypto.randomUUID()}-${file.name}`;
    const up = await supabase.storage.from("documents").upload(path, file, { contentType: file.type });
    if (up.error) { setUploading(false); return toast.error(up.error.message); }
    const { data, error } = await supabase.from("documents").insert({
      organization_id: currentOrgId, client_id: cid, name: name.trim(),
      document_type: docType, storage_path: path, mime_type: file.type, size_bytes: file.size, uploaded_by: user.id,
    }).select().single();
    setUploading(false);
    if (error) return toast.error(error.message);
    toast.success("Document uploaded");
    setFile(null); setName(""); setDocType("other"); setClientId("none"); setOpen(false);
    load();
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: cid,
      action: "document.uploaded", summary: `uploaded ${name.trim()}`,
      entity_type: "documents", entity_id: data.id });
  };

  const download = async (id) => {
    try { const r = await apiFetch(`/documents/${id}/download`); openExternal(r.url); }
    catch (e) { toast.error(e.message); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This cannot be undone.`)) return;
    await supabase.storage.from("documents").remove([d.storage_path]);
    const { error } = await supabase.from("documents").delete().eq("id", d.id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: d.client_id,
      action: "document.deleted", summary: `deleted ${d.name}`, entity_type: "documents", entity_id: d.id });
  };

  const preview = async (d) => {
    // Images and PDFs → in-app modal viewer. Office docs / CSV / TXT → open in new tab
    // (the browser can't render Office natively without an external viewer).
    const ext = fileExt(d.name);
    const inline = ["pdf", "jpg", "jpeg", "png", "txt"].includes(ext);
    setPreviewDoc(d);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const r = await apiFetch(`/documents/${d.id}/download`);
      setPreviewUrl(r.url);
      if (!inline) openExternal(r.url);
      logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: d.client_id,
        action: "document.downloaded", summary: `viewed ${d.name}`, entity_type: "documents", entity_id: d.id });
    } catch (e) { toast.error(e.message); setPreviewDoc(null); }
    finally { setPreviewLoading(false); }
  };
  const closePreview = () => { setPreviewDoc(null); setPreviewUrl(null); };

  const filtered = rows.filter((d) => {
    if (typeFilter !== "all" && d.document_type !== typeFilter) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    const clientName = d.clients ? `${d.clients.first_name} ${d.clients.last_name}`.toLowerCase() : "";
    return d.name.toLowerCase().includes(ql) || clientName.includes(ql);
  });

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="documents-page">
      <PageHeader title="Documents" subtitle="Every file in your workspace, securely stored."
        actions={!isReadOnly && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" data-testid="docs-export-csv"
              disabled={filtered.length === 0}
              onClick={() => downloadCsv(
                `documents-${iso(new Date())}`,
                [
                  { key: "name", label: "File name" },
                  { key: "document_type", label: "Type" },
                  { label: "Client", get: (d) => d.clients ? `${d.clients.first_name} ${d.clients.last_name}` : "" },
                  { key: "mime_type", label: "MIME" },
                  { key: "size_bytes", label: "Size (bytes)" },
                  { label: "Uploaded", get: (d) => iso(d.created_at) },
                  { label: "Last updated", get: (d) => iso(d.updated_at) },
                  // NOTE: storage_path & signed URLs are intentionally NOT exported.
                ],
                filtered,
              )}
            ><Download size={14} className="mr-1.5" /> Export CSV</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="upload-doc-btn" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><UploadCloud size={16} className="mr-1.5" /> Upload document</Button>
              </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle className="font-display tracking-tight">Upload document</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>File</Label>
                  <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.csv,.txt" onChange={(e) => { const f = e.target.files?.[0]; setFile(f); if (f && !name) setName(f.name); }} className="mt-1.5" data-testid="doc-file" />
                  <p className="text-xs text-[#A8A29E] mt-1">PDF, DOC/DOCX, XLS/XLSX, JPG, PNG, CSV, TXT · max 25 MB</p>
                </div>
                <div><Label>Document name *</Label><Input data-testid="doc-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={docType} onValueChange={setDocType}>
                      <SelectTrigger className="mt-1.5" data-testid="doc-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Client</Label>
                    <div className="mt-1.5">
                      <Typeahead testid="doc-client"
                        options={[
                          { value: "none", label: "— None —" },
                          ...clients.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name}`, sub: c.email || c.phone || "" })),
                        ]}
                        value={clientId}
                        onChange={(v) => setClientId(v || "none")}
                        placeholder="Search clients…" />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
                <Button data-testid="save-doc-btn" onClick={upload} disabled={uploading} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{uploading ? "Uploading…" : "Upload"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="pl-9 bg-white border-[#E8E2D9]" data-testid="docs-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48" data-testid="docs-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description="Upload intake forms, insurance, referrals and more — all in one place."
          action={!isReadOnly && (
            <Button
              onClick={() => setOpen(true)}
              data-testid="empty-cta-upload-doc"
              className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
            >
              <UploadCloud size={14} className="mr-1.5" /> Upload document
            </Button>
          )}
        />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#57534E] bg-white rounded-2xl border border-[#E8E2D9]">No documents match your filter.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] divide-y divide-[#E8E2D9]">
          {filtered.map((d) => (
            <div key={d.id} className="px-6 py-4 flex items-center gap-3 hover:bg-[#FAF8F5] transition-colors cursor-pointer" data-testid={`doc-row-${d.id}`} onClick={() => preview(d)}>
              <div className="w-10 h-10 rounded-lg bg-[#FDE9D8] flex items-center justify-center flex-shrink-0"><FileText size={16} className="text-[#E8712C]" strokeWidth={1.5} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{d.name}</div>
                <div className="text-xs text-[#57534E]">
                  <span className="capitalize">{d.document_type.replace(/_/g, " ")}</span>
                  {" · "}<span className="uppercase">{fileExt(d.name)}</span>
                  {" · "}{formatSize(d.size_bytes)}
                  {d.clients && <> · <Link to={`/app/clients/${d.clients.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{d.clients.first_name} {d.clients.last_name}</Link></>}
                  {" · "}{formatDate(d.created_at)} · {d.profiles?.full_name || "—"}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); preview(d); }} data-testid={`view-${d.id}`} title="Open"><Eye size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); download(d.id); }} data-testid={`dl-${d.id}`} title="Download"><Download size={14} /></Button>
              {!isReadOnly && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); del(d); }} className="text-red-600" data-testid={`del-${d.id}`} title="Delete"><Trash2 size={14} /></Button>}
            </div>
          ))}
        </div>
      )}

      {/* In-app document preview modal */}
      <Dialog open={!!previewDoc} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="bg-white max-w-4xl">
          <DialogHeader className="flex-row items-center justify-between">
            <div className="min-w-0">
              <DialogTitle className="font-display tracking-tight truncate">{previewDoc?.name}</DialogTitle>
              {previewDoc && (
                <div className="text-xs text-[#57534E] mt-1">
                  <span className="uppercase">{fileExt(previewDoc.name)}</span> · {formatSize(previewDoc.size_bytes)}
                  {previewDoc.clients && <> · {previewDoc.clients.first_name} {previewDoc.clients.last_name}</>}
                  {" · "}{formatDate(previewDoc.created_at)} · {previewDoc.profiles?.full_name || "—"}
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="bg-[#FAF8F5] rounded-lg overflow-hidden" style={{ minHeight: "60vh" }}>
            {previewLoading || !previewUrl ? (
              <div className="text-center py-20 text-sm text-[#57534E]">Loading preview…</div>
            ) : (() => {
              const ext = fileExt(previewDoc.name);
              if (["jpg", "jpeg", "png"].includes(ext)) {
                return <img src={previewUrl} alt={previewDoc.name} className="w-full h-auto max-h-[70vh] object-contain mx-auto" />;
              }
              if (ext === "pdf") {
                return <iframe src={previewUrl} title={previewDoc.name} className="w-full" style={{ height: "70vh", border: 0 }} />;
              }
              if (ext === "txt" || ext === "csv") {
                return <iframe src={previewUrl} title={previewDoc.name} className="w-full bg-white" style={{ height: "70vh", border: 0 }} />;
              }
              return (
                <div className="text-center py-20">
                  <FileText size={40} className="mx-auto text-[#A8A29E] mb-3" strokeWidth={1.5} />
                  <p className="text-sm text-[#57534E]">This file type can&apos;t be previewed in-app.</p>
                  <p className="text-xs text-[#A8A29E] mt-1">It has been opened in a new tab — or use Download below.</p>
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="preview-download-btn" onClick={() => previewDoc && download(previewDoc.id)} variant="outline" className="rounded-full"><Download size={13} className="mr-1.5" /> Download</Button>
            {!isReadOnly && previewDoc && (
              <Button data-testid="preview-delete-btn"
                onClick={() => { const d = previewDoc; del(d).then(() => closePreview()); }}
                variant="outline" className="rounded-full text-red-600 border-red-200"><Trash2 size={13} className="mr-1.5" /> Delete</Button>
            )}
            <Button onClick={closePreview} variant="outline" className="rounded-full"><XIcon size={13} className="mr-1.5" /> Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
